"use client";

import { cn } from "@gettactic/helpers";
import React, { useCallback, useEffect, useRef, useState } from "react";

export interface ToggleGroupItem {
  /** Identifier passed to `onValueChange`. */
  value: string;
  /**
   * Label rendered inside the toggle button. Typed as ReactNode so callers
   * can compose richer content (e.g. label + count badge) rather than only
   * plain strings.
   */
  label: React.ReactNode;
}

export interface ToggleGroupProps {
  /** The options, in display order. */
  items: ToggleGroupItem[];
  /** The selected option's `value`. */
  value: string;
  /** Called with the chosen option's `value`. */
  onValueChange: (value: string) => void;
  className?: string;
  /**
   * When true, the toggle group fills its container and each item gets an
   * equal share of the available width (`flex-1`). When false (the default)
   * the group sits inline at its intrinsic width.
   */
  fullWidth?: boolean;
  /**
   * `sm` is the compact pill for toolbars inside a page (26px items, 13px
   * labels); `default` is the regular header-sized switcher.
   */
  size?: "default" | "sm";
}

/**
 * A pill switcher between a few views or modes (List / Map, Upcoming / Past): exactly one option
 * is selected, and a white highlight slides to it.
 */
export const ToggleGroup: React.FC<ToggleGroupProps> = ({
  items,
  value,
  onValueChange,
  className,
  fullWidth = false,
  size = "default"
}) => {
  const isSmall = size === "sm";
  const containerRef = useRef<HTMLDivElement>(null);
  const [sliderStyle, setSliderStyle] = useState<React.CSSProperties>({});

  const updateSlider = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const activeIndex = items.findIndex((item) => item.value === value);
    if (activeIndex === -1) return;

    const buttons = container.querySelectorAll<HTMLButtonElement>(
      "[data-toggle-item]"
    );
    const activeButton = buttons[activeIndex];
    if (!activeButton) return;

    setSliderStyle({
      left: `${activeButton.offsetLeft}px`,
      width: `${activeButton.offsetWidth}px`
    });
  }, [items, value]);

  useEffect(() => {
    updateSlider();
  }, [updateSlider]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative items-center rounded-full bg-neutral-100",
        isSmall ? "gap-0.5 p-[3px]" : "p-1",
        fullWidth ? "flex w-full" : "inline-flex",
        className
      )}
    >
      {/* Sliding background */}
      <div
        className={cn(
          "absolute rounded-full bg-white shadow-sm transition-all duration-200 ease-in-out",
          isSmall ? "top-[3px] bottom-[3px]" : "top-1 bottom-1"
        )}
        style={sliderStyle}
      />

      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          data-toggle-item
          aria-pressed={value === item.value}
          onClick={() => onValueChange(item.value)}
          className={cn(
            "relative z-10 cursor-pointer rounded-full font-medium transition-colors",
            isSmall ? "h-6.5 px-3 text-control" : "px-4 py-1.5 text-sm",
            fullWidth && "flex-1",
            value === item.value
              ? "text-neutral-900"
              : "text-neutral-800 hover:text-neutral-1000"
          )}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
};
